import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse } from '@/api/types';
import { useAuth } from '@/context/AuthContext';
import { Loader2, MapPin } from 'lucide-react';
import clsx from 'clsx';

interface LocationStepProps {
  onComplete: () => void;
}

interface Country {
  code: string;
  name: string;
  priority: number;
}

const DURATION_OPTIONS = [
  { value: 'less_than_1_year', label: 'Less than 1 year' },
  { value: 'one_to_3_years', label: '1-3 years' },
  { value: 'three_to_5_years', label: '3-5 years' },
  { value: 'five_to_10_years', label: '5-10 years' },
  { value: 'more_than_10_years', label: '10+ years' },
];

export default function LocationStep({ onComplete }: LocationStepProps) {
  const { user } = useAuth();
  const [country, setCountry] = useState(user?.country || 'US');
  const [state, setState] = useState(user?.state || '');
  const [city, setCity] = useState(user?.city || '');
  const [duration, setDuration] = useState('');

  // Fetch countries
  const { data: countriesData } = useQuery({
    queryKey: ['reference', 'countries'],
    queryFn: () => api.get<ApiResponse<{ items: Country[] }>>('/reference/countries'),
  });

  const countries = countriesData?.data?.items || [];

  useEffect(() => {
    if (user) {
      if (user.country) setCountry(user.country);
      if (user.state) setState(user.state);
      if (user.city) setCity(user.city);
    }
  }, [user]);

  const saveStep = useMutation({
    mutationFn: async () => {
      return api.patch('/onboarding/step/3', {
        country,
        state,
        city,
        durationLivingHere: duration,
      });
    },
    onSuccess: () => {
      onComplete();
    },
  });

  const handleSubmit = () => {
    if (!country || !state || !city || !duration) return;
    saveStep.mutate();
  };

  const isValid = country && state.length > 0 && city.length > 0 && duration;

  return (
    <div className="space-y-6">
      {/* Icon Header */}
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center">
          <MapPin className="w-8 h-8 text-accent-600" />
        </div>
      </div>

      {/* Country */}
      <div>
        <label htmlFor="country" className="label">
          Country *
        </label>
        <select
          id="country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="input"
        >
          <option value="">Select country</option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* State */}
      <div>
        <label htmlFor="state" className="label">
          State/Province *
        </label>
        <input
          id="state"
          type="text"
          value={state}
          onChange={(e) => setState(e.target.value)}
          placeholder="e.g., California, Ontario"
          className="input"
        />
      </div>

      {/* City */}
      <div>
        <label htmlFor="city" className="label">
          City *
        </label>
        <input
          id="city"
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="e.g., San Francisco, Toronto"
          className="input"
        />
      </div>

      {/* Duration */}
      <div>
        <label className="label">How long have you lived here? *</label>
        <div className="space-y-2">
          {DURATION_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setDuration(option.value)}
              className={clsx(
                'w-full p-3 rounded-lg border-2 text-left text-sm font-medium transition-all',
                duration === option.value
                  ? 'border-accent-500 bg-accent-50 text-accent-700'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Submit */}
      <div className="pt-4">
        <button
          onClick={handleSubmit}
          disabled={!isValid || saveStep.isPending}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {saveStep.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Continue'
          )}
        </button>
        {saveStep.isError && (
          <p className="text-sm text-red-600 mt-2 text-center">
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
